import isEqual from 'lodash/isEqual';
import LZString from 'lz-string';
import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import type { RevelioPageId } from '../../components/PageConfigProvider/types';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { isTaxonomyFilterId } from '../constants/filter-constants';
import type { ConsistencyKey } from '../engine/filterGroupConsistency';
import { getFilterDefinition } from '../engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  type LinkedTaxonomyFilter,
} from '../types/filters';
import { decodeFiltersParam } from '../utils/savedViewUrl';

const equalsDefinition = (filter: Filter): boolean => {
  const definition = getFilterDefinition(filter.id) as
    | Record<string, unknown>
    | undefined;
  if (!definition) return false;
  const current = filter as unknown as Record<string, unknown>;
  const keys = new Set([...Object.keys(current), ...Object.keys(definition)]);
  return [...keys].every((key) => {
    const value = current[key];
    const base = definition[key];
    // Functions (a tree's `branches` labeller) are display config, not
    // state: JSON drops them.
    if (typeof value === 'function' || typeof base === 'function') return true;
    if (Array.isArray(value) && value.length === 0 && base === undefined) {
      return true;
    }
    return isEqual(value, base);
  });
};

const isOmittable = (id: ConsistencyKey, filter: Filter): boolean =>
  filter.isPrimary !== true &&
  !isTaxonomyFilterId(id) &&
  filter.type !== FilterType.MULTI &&
  equalsDefinition(filter);

const withAdaptiveRecord = (id: ConsistencyKey, filter: Filter): Filter => {
  if (!isTaxonomyFilterId(id)) return filter;
  const { isLinkApplied: _linkApplied, ...rest } = filter;
  if (typeof rest.isUserSubmitted !== 'boolean' || rest.isAiApplied === true) {
    return rest as Filter;
  }
  return {
    ...rest,
    isAdaptive: !rest.isUserSubmitted,
  } as LinkedTaxonomyFilter;
};

const pageKeyOf = (entries: [ConsistencyKey, Filter][]): string =>
  JSON.stringify(
    entries.map(([id, filter]) => {
      if (!isTaxonomyFilterId(id)) return [id, filter];
      const {
        isAdaptive: _isAdaptive,
        isUserSubmitted: _isUserSubmitted,
        ...rest
      } = filter as LinkedTaxonomyFilter;
      return [id, rest];
    })
  );

const toSerializable = (
  entries: [ConsistencyKey, Filter][],
  isScreenerPage: boolean,
  versionByTaxonomyFilterId: Partial<Record<ConsistencyKey, string>>
): [ConsistencyKey, Filter][] => {
  const changedFilters = entries
    .filter(([id, filter]) => !isOmittable(id, filter))
    .map(([id, filter]): [ConsistencyKey, Filter] => [
      id,
      withAdaptiveRecord(id, filter),
    ]);

  if (isScreenerPage) {
    return changedFilters.filter(([id]) => !isTaxonomyFilterId(id));
  }

  return changedFilters.map(([id, filter]): [ConsistencyKey, Filter] => {
    const version = versionByTaxonomyFilterId[id];
    if (version && 'value' in filter && filter.value) {
      return [id, { ...filter, value: { ...filter.value, version } } as Filter];
    }
    return [id, filter];
  });
};

export const useSyncFiltersToUrl = (
  filtersState: [ConsistencyKey, Filter][],
  roleTaxonomyVersion?: string,
  skillTaxonomyVersion?: string,
  activityTaxonomyVersion?: string,
  revelioPageId?: RevelioPageId,
  enabled = true
) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (location.pathname.startsWith('/report-builder')) {
      return;
    }

    // Bail when a navigation AWAY from this page is in flight. This provider
    // can still be mounted (and re-fire this effect from a late filter settle,
    // e.g. correction-preference sync landing under load) after the browser URL
    // has already moved to another route. The `setSearchParams` setter is bound
    // to THIS page's react-router location, so writing now would `replace` the
    // new route's URL back to this page — silently reverting an external
    // navigation (e.g. a global-AI-assistant `navigate`). Comparing the live
    // browser pathname against the router pathname this effect captured detects
    // that mismatch; in steady state they are identical, so this is a no-op.
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== location.pathname
    ) {
      return;
    }

    const currentSerializedFilters = searchParams.get('filters');
    const currentSerializedFilterGroups = searchParams.get('filterGroups');
    if (currentSerializedFilterGroups) return;

    const isScreenerPage = isScreenerV2Page(revelioPageId);

    // For screener pages: exclude taxonomy filters (matching old screener behavior)
    // For other pages: include taxonomy filters with version info
    const versionByTaxonomyFilterId: Partial<Record<ConsistencyKey, string>> = {
      [FilterId.ROLE_TAXONOMY]: roleTaxonomyVersion,
      [FilterId.SKILL_TAXONOMY]: skillTaxonomyVersion,
      [FilterId.ACTIVITY_TAXONOMY]: activityTaxonomyVersion,
    };

    const filtersToSerialize = toSerializable(
      filtersState,
      isScreenerPage,
      versionByTaxonomyFilterId
    );

    const newSerializedFilters = LZString.compressToEncodedURIComponent(
      JSON.stringify(filtersToSerialize)
    );

    if (currentSerializedFilters !== newSerializedFilters) {
      const currentFilters = decodeFiltersParam(currentSerializedFilters);
      const isFormatNormalization =
        currentFilters !== null &&
        pageKeyOf(
          toSerializable(
            currentFilters,
            isScreenerPage,
            versionByTaxonomyFilterId
          )
        ) === pageKeyOf(filtersToSerialize);
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set('filters', newSerializedFilters);
          // Reset pagination when filters change to keep results consistent.
          if (!isFormatNormalization) {
            nextParams.delete('page');
          }
          return nextParams;
        },
        { replace: true }
      );
    }
  }, [
    enabled,
    filtersState,
    searchParams,
    setSearchParams,
    roleTaxonomyVersion,
    skillTaxonomyVersion,
    activityTaxonomyVersion,
    revelioPageId,
    location.pathname,
  ]);
};
